import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase }             from '@/shared/lib/supabase';
import { polaroidRepository }   from '../../data/polaroidRepository';

export interface PolaroidItem {
  id:            string;
  url:           string; // base64 data URL
  identificacao: string | null;
}

export function usePolaroidQueue(telaId: string) {
  const [fila, setFila] = useState<PolaroidItem[]>([]);
  // Uma foto aprovada aparece uma vez: outro UPDATE na mesma linha (ainda
  // `aprovada`) não a reexibe.
  const exibidasRef = useRef<Set<string>>(new Set());

  const enqueueRef = useRef<(item: PolaroidItem) => void>(undefined);
  const enqueue = useCallback((item: PolaroidItem) => {
    setFila(prev => [...prev, item]);
  }, []);
  useEffect(() => { enqueueRef.current = enqueue; }, [enqueue]);

  useEffect(() => {
    if (!telaId) return;

    const canal = supabase
      .channel(`polaroid_tv_${telaId}`)
      .on(
        'broadcast',
        { event: 'polaroid_aprovada' },
        async ({ payload }) => {
          const id = (payload as { id?: string } | null)?.id;
          if (!id || exibidasRef.current.has(id)) return;
          exibidasRef.current.add(id);

          // Broadcast só traz o id; photo_data (base64) vem da RPC.
          const data = await polaroidRepository.buscarPhotoData(telaId, id);

          if (!data?.photo_data) return;
          enqueueRef.current?.({ id, url: data.photo_data, identificacao: data.identificacao });
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(canal); };
  }, [telaId]);

  // Exibe fila[0] por 12s, depois remove e passa para o próximo
  const atual = fila[0] ?? null;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!atual) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => setFila(prev => prev.slice(1)),
      12_000,
    );
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // Chave pelo id: duas fotos seguidas com a mesma url não reiniciavam o
    // timer e a segunda ficava na tela para sempre.
  }, [atual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { atual };
}
